/**
 * Yotpo Script Loader Utility
 * 
 * Manages loading and initialization of the Yotpo widgets script.
 * Ensures the script loads only once and provides promise-based API.
 */

// Script loading state
let scriptLoadingState = {
  loaded: false,
  loading: false,
  error: null,
  loadPromise: null,
};

// Listeners for script load events
const loadListeners = [];
const errorListeners = [];

/**
 * Check if Yotpo API is available
 * @returns {boolean}
 */
export const isYotpoReady = () => {
  return !!(window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === 'function');
};

/**
 * Check if script is already loaded in the DOM
 * @returns {boolean}
 */
const isScriptInDOM = () => {
  return !!document.getElementById('yotpo-widget-script');
};

/**
 * Wait for yotpoWidgetsContainer to be available
 * @param {number} timeout - Maximum wait time in milliseconds
 * @param {number} interval - Check interval in milliseconds
 * @returns {Promise<boolean>}
 */
const waitForYotpoContainer = (timeout = 10000, interval = 100) => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    
    const checkContainer = () => {
      if (isYotpoReady()) {
        resolve(true);
        return;
      }
      
      if (Date.now() - startTime > timeout) {
        reject(new Error('Yotpo container timeout - window.yotpoWidgetsContainer not available'));
        return;
      }
      
      setTimeout(checkContainer, interval);
    };
    
    checkContainer();
  });
};

/**
 * Load Yotpo script dynamically
 * @param {string} appKey - Your Yotpo app key
 * @param {Object} options - Configuration options
 * @param {number} options.timeout - Maximum wait time for container (default: 10000ms)
 * @returns {Promise<boolean>}
 */
export const loadYotpoScript = (appKey, options = {}) => {
  const { timeout = 10000 } = options;

  // Return existing promise if already loading
  if (scriptLoadingState.loading && scriptLoadingState.loadPromise) {
    return scriptLoadingState.loadPromise;
  }

  // Return resolved promise if already loaded
  if (scriptLoadingState.loaded && isYotpoReady()) {
    return Promise.resolve(true);
  }

  // Check if script exists but container isn't ready yet
  if (isScriptInDOM() && !isYotpoReady()) {
    scriptLoadingState.loading = true;
    scriptLoadingState.loadPromise = waitForYotpoContainer(timeout)
      .then(() => {
        scriptLoadingState.loaded = true;
        scriptLoadingState.loading = false;
        notifyLoadListeners();
        return true;
      })
      .catch((error) => {
        scriptLoadingState.loading = false;
        scriptLoadingState.error = error;
        notifyErrorListeners(error);
        throw error;
      });
    
    return scriptLoadingState.loadPromise;
  }

  // Create and inject script
  scriptLoadingState.loading = true;
  scriptLoadingState.error = null;

  scriptLoadingState.loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = 'yotpo-widget-script';
    script.src = `https://cdn-widgetsrepository.yotpo.com/v1/loader/${appKey}`;
    script.async = true;

    script.onload = () => {
      // Wait for yotpoWidgetsContainer to be available
      waitForYotpoContainer(timeout)
        .then(() => {
          scriptLoadingState.loaded = true;
          scriptLoadingState.loading = false;
          notifyLoadListeners();
          resolve(true);
        })
        .catch((error) => {
          scriptLoadingState.loading = false;
          scriptLoadingState.error = error;
          notifyErrorListeners(error);
          reject(error);
        });
    };

    script.onerror = (error) => {
      scriptLoadingState.loading = false;
      scriptLoadingState.error = new Error('Failed to load Yotpo script');
      notifyErrorListeners(scriptLoadingState.error);
      reject(scriptLoadingState.error);
      
      // Clean up failed script
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };

    document.head.appendChild(script);
  });

  return scriptLoadingState.loadPromise;
};

/**
 * Initialize Yotpo widgets in the DOM
 * @returns {boolean} - Success status
 */
export const initializeYotpoWidgets = () => {
  if (!isYotpoReady()) {
    console.warn('Yotpo not ready - cannot initialize widgets');
    return false;
  }

  try {
    window.yotpoWidgetsContainer.initWidgets();
    return true;
  } catch (error) {
    console.error('Error initializing Yotpo widgets:', error);
    return false;
  }
};

/**
 * Add listener for script load event
 * @param {Function} callback
 */
export const onYotpoLoad = (callback) => {
  if (scriptLoadingState.loaded && isYotpoReady()) {
    callback();
  } else {
    loadListeners.push(callback);
  }
};

/**
 * Add listener for script error event
 * @param {Function} callback
 */
export const onYotpoError = (callback) => {
  if (scriptLoadingState.error) {
    callback(scriptLoadingState.error);
  } else {
    errorListeners.push(callback);
  }
};

/**
 * Notify all load listeners
 */
const notifyLoadListeners = () => {
  loadListeners.forEach((callback) => {
    try {
      callback();
    } catch (error) {
      console.error('Error in Yotpo load listener:', error);
    }
  });
  loadListeners.length = 0; // Clear listeners
};

/**
 * Notify all error listeners
 */
const notifyErrorListeners = (error) => {
  errorListeners.forEach((callback) => {
    try {
      callback(error);
    } catch (err) {
      console.error('Error in Yotpo error listener:', err);
    }
  });
  errorListeners.length = 0; // Clear listeners
};

/**
 * Get current loading state
 * @returns {Object}
 */
export const getYotpoState = () => {
  return {
    loaded: scriptLoadingState.loaded,
    loading: scriptLoadingState.loading,
    error: scriptLoadingState.error,
    ready: isYotpoReady(),
  };
};

/**
 * Reset Yotpo state (useful for testing)
 * WARNING: Only use this if you know what you're doing
 */
export const resetYotpoState = () => {
  scriptLoadingState = {
    loaded: false,
    loading: false,
    error: null,
    loadPromise: null,
  };
  loadListeners.length = 0;
  errorListeners.length = 0;
};
